import { action, computed, makeObservable, observable } from "mobx"

import {
  cardMassPayoutsApi,
  catchErrors,
  massPayoutsApi,
} from "@src/shared/api"
import {
  CardMassPayoutResponsePayouts,
  MassPayoutResponsePayouts,
} from "@src/shared/api/generated"
import { loading } from "@src/shared/classes/utils/loading"
import { wrapSingleton } from "@src/shared/classes/utils/wrapSingleton"

type PayoutsConformationProcessState = {
  id: string
  payouts: (CardMassPayoutResponsePayouts | MassPayoutResponsePayouts)[]
  type: "card" | "crypto"
}

const defaultState: PayoutsConformationProcessState = {
  id: "",
  payouts: [],
  type: "card",
}

export class PayoutsConformationProcess extends loading(["confirm", "cancel"]) {
  _state: PayoutsConformationProcessState = {
    ...defaultState,
  }

  constructor() {
    super()

    makeObservable(this, {
      _cardCancel: action.bound,
      _cardConfirm: action.bound,
      _cryptoCancel: action.bound,
      _cryptoConfirm: action.bound,
      _state: observable.deep,
      cancel: action.bound,
      confirm: action.bound,
      payouts: computed,
      reset: action.bound,
      setState: action.bound,
      type: computed,
    })
  }

  async _cardCancel() {
    try {
      const {
        data: { success },
      } = await cardMassPayoutsApi.cardMassPayoutsDelete(this.id)

      return success
    } catch (e: any) {
      catchErrors(e)
    }

    return false
  }

  async _cardConfirm(otp: string) {
    const {
      data: { data },
    } = await cardMassPayoutsApi.cardMassPayoutsSend(this.id, {
      otp,
    })

    return Boolean(data)
  }

  async _cryptoCancel() {
    try {
      const {
        data: { success },
      } = await massPayoutsApi._delete(this.id)

      return success
    } catch (e: any) {
      catchErrors(e)
    }

    return false
  }

  async _cryptoConfirm(otp: string): Promise<any> {
    if (!this.id) {
      return Promise.resolve()
    }

    const {
      data: { data },
    } = await massPayoutsApi.send(this.id, {
      otp,
    })

    return Boolean(data)
  }

  async cancel() {
    if (!this.id) return Promise.resolve()

    const method = {
      card: () => this._cardCancel(),
      crypto: () => this._cryptoCancel(),
    }[this.type]

    if (typeof method === "function") {
      return method()
    }

    return Promise.resolve()
  }

  async confirm(otp: string) {
    if (!this.id) return Promise.resolve()

    const method = {
      card: () => this._cardConfirm(otp),
      crypto: () => this._cryptoConfirm(otp),
    }[this.type]

    if (typeof method === "function") {
      return method()
    }

    return Promise.resolve()
  }

  reset() {
    this.setState(defaultState)
    this.resetLoadersState()
  }

  setState(state: PayoutsConformationProcessState) {
    this._state = state
  }

  get id() {
    return this._state.id
  }

  get payouts() {
    return this._state.payouts
  }

  get type() {
    return this._state.type
  }
}

export const GlobalPayoutsConformationProcess =
  wrapSingleton<PayoutsConformationProcess>(PayoutsConformationProcess)
